/**
 * Client Reports API (MR Inc 4.6). Backend router: app/routers/client_reports.py,
 * prefix /client-reports (all AdminUser). Page-1 publishing + batch produce.
 *
 * Template downloads stream a blob (downloadBlob, mirrors iaaExports); the page-1
 * upload + produce use a raw multipart fetch (the JSON `api` client only does JSON);
 * the list + purge use the JSON `api` client.
 */
import { api } from './client';
import { downloadBlob } from './iaaExports';
import type { ISODate, ISODateTime, UUID } from './types';

export interface Page1Preview {
  period_month: ISODate;
  prose: { pt: string[]; en: string[] };  // bilingual (Inc 6c)
  stats: unknown[];
  overwrite: boolean;
}
export interface Page1Commit {
  id: UUID;
  period_month: ISODate;
  superseded: boolean;
  prose_paragraphs: number;
  stats_sections: number;
}
export interface Page1Content {
  id: UUID;
  period_month: ISODate;
  is_active: boolean;
  word_filename: string;
  xlsx_filename: string;
  prose: { pt: string[]; en: string[] };  // bilingual (Inc 6c)
  stats: unknown[];
  created_at: ISODateTime;
}
export interface Page1Month {
  id: UUID;
  period_month: ISODate;
  word_filename: string;
  xlsx_filename: string;
  created_at: ISODateTime;
}
/** MR track-record window presets (Batch produce) — year-scale, distinct from the
 * on-screen AuM & Return picker (Pedro 2026-08-25). */
export type MrTrackPreset = '12m' | '3y' | '5y' | 'max' | 'since';

/** MR layout: A current page 2 / B since-inception + periods / C = B + portfolio pages (MR-C). */
export type MrTemplate = 'A' | 'B' | 'C';

export interface ProduceItem {
  agreement_id: UUID;
  code: string | null;
  mnemonic: string | null;
  /** Template C: why the report has no portfolio page. */
  reason?: string | null;
}
export interface ProduceReport {
  period_month: ISODate;
  mode: 'preview' | 'commit';
  page1_present: boolean;
  produced: ProduceItem[];
  overwritten: ProduceItem[];
  skipped_no_data: ProduceItem[];
  /** Template C (MC6): produced as B, without a portfolio page, each with its reason. */
  no_portfolio_page: ProduceItem[];
}
export interface ProducedReportItem {
  agreement_id: UUID;
  code: string | null;
  mnemonic: string | null;
  pdf_filename: string;
  created_at: ISODateTime;
}
export interface RecordedMonth {
  period_month: ISODate;
  pdf_filename: string;
  created_at: ISODateTime;
}

function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

async function errorDetail(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

async function download(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(path, { method: 'GET', credentials: 'include' });
  if (!res.ok) throw new Error(await errorDetail(res, 'The file could not be downloaded.'));
  const cd = res.headers.get('Content-Disposition') ?? '';
  downloadBlob(await res.blob(), fileNameFromCD(cd, fallbackName));
}

async function getBlob(path: string, fallbackErr: string): Promise<Blob> {
  const res = await fetch(path, { method: 'GET', credentials: 'include' });
  if (!res.ok) throw new Error(await errorDetail(res, fallbackErr));
  return res.blob();
}

const mm = (month: number) => String(month).padStart(2, '0');

export const clientReportsApi = {
  downloadPage1Template(): Promise<void> {
    return download('/client-reports/page1-template.docx', 'page1_review_template.docx');
  },

  downloadStatsTemplate(): Promise<void> {
    return download('/client-reports/stats-template.xlsx', 'page1_stats_template.xlsx');
  },

  async uploadPage1(
    year: number,
    month: number,
    mode: 'preview' | 'commit',
    word: File,
    stats: File,
  ): Promise<Page1Preview | Page1Commit> {
    const form = new FormData();
    form.append('year', String(year));
    form.append('month', String(month));
    form.append('mode', mode);
    form.append('word', word);
    form.append('stats', stats);
    const res = await fetch('/client-reports/page1', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await errorDetail(res, 'The page-1 content could not be processed.'));
    return res.json();
  },

  async getPage1(year: number, month: number): Promise<Page1Content | null> {
    const res = await fetch(
      `/client-reports/page1?year=${year}&month=${month}`,
      { method: 'GET', credentials: 'include' },
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await errorDetail(res, 'Could not load the page-1 content.'));
    return res.json();
  },

  listMonths(): Promise<Page1Month[]> {
    return api.get<Page1Month[]>('/client-reports/page1/months');
  },

  async produce(
    year: number,
    month: number,
    mode: 'preview' | 'commit',
    trackPreset: MrTrackPreset = '5y',
    trackSince: string | null = null,
    template: MrTemplate = 'A',
  ): Promise<ProduceReport> {
    const form = new FormData();
    form.append('year', String(year));
    form.append('month', String(month));
    form.append('mode', mode);
    form.append('track_preset', trackPreset);
    if (trackSince) form.append('track_since', trackSince);
    form.append('template', template);
    const res = await fetch('/client-reports/produce', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await errorDetail(res, 'The reports could not be produced.'));
    return res.json();
  },

  purgePage1(id: UUID): Promise<void> {
    return api.delete<void>(`/client-reports/page1/${id}`);
  },

  // A 1-page PDF preview of an upload (before commit) — returns a Blob to embed.
  // `lang` picks the PT/EN side (stats + free text) so the Admin can eyeball both (Inc 6c).
  async previewPdf(
    year: number, month: number, word: File, stats: File, lang: 'pt' | 'en' = 'pt',
  ): Promise<Blob> {
    const form = new FormData();
    form.append('year', String(year));
    form.append('month', String(month));
    form.append('word', word);
    form.append('stats', stats);
    form.append('lang', lang);
    const res = await fetch('/client-reports/page1/preview.pdf', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) throw new Error(await errorDetail(res, 'Could not render the preview.'));
    return res.blob();
  },

  // The active page-1 for a month rendered as a 1-page PDF (view on-file) — a Blob to embed.
  page1Pdf(year: number, month: number, lang: 'pt' | 'en' = 'pt'): Promise<Blob> {
    return getBlob(`/client-reports/page1.pdf?year=${year}&month=${month}&lang=${lang}`,
      'Could not load the page-1 PDF.');
  },

  // Archive — by month: the produced per-agreement reports, sorted by CIIF number.
  listProduced(year: number, month: number): Promise<ProducedReportItem[]> {
    return api.get<ProducedReportItem[]>(`/client-reports/produced?year=${year}&month=${month}`);
  },

  downloadProducedZip(year: number, month: number): Promise<void> {
    return download(`/client-reports/produced.zip?year=${year}&month=${month}`,
      `Baluarte_MR_${year}-${mm(month)}.zip`);
  },

  // Archive — by agreement: the months this agreement has a recorded report for.
  listAgreementReports(agreementId: UUID): Promise<RecordedMonth[]> {
    return api.get<RecordedMonth[]>(`/agreements/${agreementId}/monthly-reports`);
  },

  downloadRecorded(agreementId: UUID, year: number, month: number): Promise<void> {
    return download(
      `/agreements/${agreementId}/recorded-monthly-report.pdf?year=${year}&month=${month}`,
      `Baluarte_MR_${year}-${mm(month)}.pdf`,
    );
  },
};
